const APP_TIMEZONE = "Asia/Riyadh";

export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    ...options,
  }).format(d);
}

export function formatDateShort(date: Date | string): string {
  return formatDate(date, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDateLong(date: Date | string): string {
  return formatDate(date, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatTime(date: Date | string): string {
  return formatDate(date, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatTimeRange(start: Date | string, end: Date | string): string {
  return `${formatTime(start)} - ${formatTime(end)}`;
}

export function formatDateTime(date: Date | string): string {
  return formatDate(date, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function getLocalDateString(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);

  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

export function getLocalDayOfWeek(date: Date | string): number {
  const d = typeof date === "string" ? new Date(date) : date;
  const dayStr = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    weekday: "short",
  }).format(d);

  const days: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  return days[dayStr] ?? 0;
}

export function localDateToUTCRange(localDateStr: string): { start: Date; end: Date } {
  const [year, month, day] = localDateStr.split("-").map(Number);

  const startLocal = new Date(`${localDateStr}T00:00:00`);
  const endLocal = new Date(`${localDateStr}T23:59:59.999`);

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const getTimezoneOffset = (date: Date): number => {
    const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
    const tzDate = new Date(date.toLocaleString("en-US", { timeZone: APP_TIMEZONE }));
    return (utcDate.getTime() - tzDate.getTime()) / (1000 * 60);
  };

  const startUTC = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
  const riyadhOffset = 3 * 60;
  startUTC.setMinutes(startUTC.getMinutes() - riyadhOffset);

  const endUTC = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
  endUTC.setMinutes(endUTC.getMinutes() - riyadhOffset);

  return { start: startUTC, end: endUTC };
}

export function createSlotDateTime(dateStr: string, timeStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes] = timeStr.split(":").map(Number);

  const riyadhOffset = 3 * 60;
  const utc = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
  utc.setMinutes(utc.getMinutes() - riyadhOffset);

  return utc;
}

export function getTodayLocalDate(): string {
  return getLocalDateString(new Date());
}

export { APP_TIMEZONE };
